/**
 * Extract a 512 px JPEG thumbnail from any File the editor uploads,
 * so the upload-time AI pre-processor (ARIA) can analyze the content
 * while the rest of the chunks are still on the wire.
 *
 * Inputs:
 *   • Image files (jpg/png/webp/heic*) → downsized via <img> + canvas.
 *     HEIC technically needs server-side decoding (browsers don't render
 *     it natively on most platforms) — we still try and let the caller
 *     handle the rejection.
 *   • Video files (mp4/mov/webm/m4v) → first decodable frame extracted
 *     via <video> + canvas. Seeks to 0.1s so we don't capture a black
 *     keyframe at t=0.
 *
 * Returns a Blob (image/jpeg, ~50 KB) or null on failure. Never throws.
 */

const THUMB_MAX_EDGE = 512;
const THUMB_JPEG_QUALITY = 0.78;
const VIDEO_SEEK_SECONDS = 0.1;

function _drawToThumb(source, sourceWidth, sourceHeight) {
  const scale = Math.min(1, THUMB_MAX_EDGE / Math.max(sourceWidth, sourceHeight));
  const w = Math.max(1, Math.round(sourceWidth * scale));
  const h = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(source, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob || null), "image/jpeg", THUMB_JPEG_QUALITY);
  });
}

async function _imageFileToThumb(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      try {
        const blob = await _drawToThumb(img, img.naturalWidth, img.naturalHeight);
        resolve(blob);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null); // HEIC or unsupported — caller skips preview.
    };
    img.src = url;
  });
}

async function _videoFileToThumb(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true; // required for autoplay-less seek on some browsers
    video.playsInline = true;
    video.crossOrigin = "anonymous";
    let settled = false;

    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.remove();
    };
    const finish = (blob) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(blob);
    };

    video.onloadeddata = () => {
      try {
        // Seek away from t=0 so we don't capture a black keyframe.
        const target = Math.min(VIDEO_SEEK_SECONDS, (video.duration || 0) / 4);
        video.currentTime = target > 0 ? target : 0;
      } catch {
        finish(null);
      }
    };
    video.onseeked = async () => {
      try {
        const blob = await _drawToThumb(video, video.videoWidth, video.videoHeight);
        finish(blob);
      } catch {
        finish(null);
      }
    };
    video.onerror = () => finish(null);
    // Safety timeout — some MOV files refuse to decode in-browser
    // (HEVC, ProRes). Give up after 6s so the upload doesn't stall
    // waiting on a preview that'll never arrive.
    setTimeout(() => finish(null), 6000);

    video.src = url;
  });
}

export async function extractPreviewThumbnail(file) {
  if (!file || typeof file !== "object") return null;
  const type = file.type || "";
  try {
    if (type.startsWith("image/")) return await _imageFileToThumb(file);
    if (type.startsWith("video/")) return await _videoFileToThumb(file);
  } catch {
    return null;
  }
  return null;
}
